import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import apiClient from "../../../axiosClient";
import { CampaignConfigDetailsProps } from "../../../interfaces/campaignConfig";
import useIsMounted from "../../../utils/useMountedRef";

interface TemplateUserMapping {
    ce_username: string;
    tcn_agent_id: string;
    tcn_agent_username: string;
}

const CallCampaignDetails: React.FC<CampaignConfigDetailsProps> = ({ inputsState }) => {
    const templateName = inputsState['campaign_config_name'] || '';
    const [templateUsers, setTemplateUsers] = useState<TemplateUserMapping[]>([]);
    const [loading, setLoading] = useState(false);
    const isMountedRef = useIsMounted();

    useEffect(() => {
        if (templateName) {
            fetchTemplateUsers(templateName);
        } else {
            setTemplateUsers([]);
        }
    }, [templateName]);

    const fetchTemplateUsers = async (name: string) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/api/v1/tcn_config/tcn_template/${name}/users?is_active=true`);
            if (response.status === 200) {
                if (!isMountedRef.current) return;
                setTemplateUsers(response.data?.users || []);
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error('Error fetching template users:', error);
            setTemplateUsers([]);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    return (
        <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>Agent to User Mappings</Typography>
            {loading ? (
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading mappings...</Typography>
                </Box>
            ) : templateUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No agent mappings found for this template</Typography>
            ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 1 }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1 }}>
                                <Typography variant="caption" fontWeight={600}>Agent</Typography>
                            </Box>
                            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1 }}>
                                <Typography variant="caption" fontWeight={600}>User</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {templateUsers.map((mapping, index) => (
                            <Box 
                                component="tr" 
                                key={mapping.tcn_agent_id}
                                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                            >
                                <Box component="td" sx={{ py: 1, px: 1 }}>
                                    <Chip 
                                        label={`Agent ${index + 1}`} 
                                        color="secondary" 
                                        size="small" 
                                        variant="outlined" 
                                    />
                                </Box>
                                <Box component="td" sx={{ py: 1, px: 1 }}>
                                    {mapping.ce_username ? (
                                        <Chip 
                                            label={mapping.ce_username} 
                                            color="primary" 
                                            size="small" 
                                            variant="outlined" 
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No user assigned</Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
 
export default CallCampaignDetails;
